import React, { useEffect, useState } from 'react';
import { X, Loader2, AlertTriangle, Check, User, Shield, Smile, Lock, Sparkles, FileLock } from 'lucide-react';
import { api, type Profile } from '../lib/api';
import { Avatar } from './Avatar';
import { AvatarCustomizer } from './AvatarCustomizer';
import { CountryField, Field, INPUT } from './ProfileFields';
import { AiKeyPanel } from './AiKeyPanel';
import { AccountDataPanel } from './AccountDataPanel';
import type { AvatarConfig } from '../types';

interface Props {
  profile: Profile;
  onProfileChange: (p: Profile) => void;
  onClose: () => void;
  onAccountDeleted: () => void;
  /** Con qué pestaña abrir. Por defecto, los datos. */
  pestanaInicial?: Pestana;
}

type Pestana = 'datos' | 'avatar' | 'ia' | 'seguridad' | 'privacidad';

/**
 * Editor del perfil.
 *
 * Antes esto era un lápiz diminuto en una esquina que abría el personalizador
 * de avatar con un campo de nombre encima. Nada decía que ahí se cambiaba el
 * nombre público, y el país no se podía tocar en ningún sitio.
 *
 * Tres pestañas porque son tres tareas distintas y ninguna necesita a las
 * otras: cambiar cómo te llamas, cambiar cómo te ves, y cambiar la contraseña.
 * Cada una guarda por su cuenta, así que un fallo al cambiar la contraseña no
 * se lleva por delante el nombre que acababas de escribir.
 */
export const EditProfileModal: React.FC<Props> = ({
  profile,
  onProfileChange,
  onClose,
  onAccountDeleted,
  pestanaInicial,
}) => {
  const [pestana, setPestana] = useState<Pestana>(pestanaInicial ?? 'datos');

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/85 backdrop-blur-sm flex items-start sm:items-center-safe justify-center p-4 overflow-y-auto"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="editar-titulo"
    >
      <div
        className={`surface-raised bg-ink-900 w-full my-auto animate-pop transition-[max-width] duration-300 ${
          // El taller de personaje necesita sitio para ir en dos columnas.
          pestana === 'avatar' ? 'max-w-3xl' : 'max-w-lg'
        }`}
      >
        <header className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="flex-1 min-w-0">
            <h2 id="editar-titulo" className="font-display font-semibold text-[1.0625rem] text-ink-50">
              Editar tu perfil
            </h2>
            <p className="text-ink-400 text-[0.8125rem] leading-snug mt-0.5">
              {profile.username} · en Open Dahu desde{' '}
              {new Date(profile.createdAt).toLocaleDateString('es', {
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="-mt-1 -mr-2 p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </header>

        <div className="px-6">
          <div role="tablist" className="flex gap-1 p-1 rounded-lg bg-ink-850 border border-ink-700">
            <Pestanya actual={pestana} valor="datos" onClick={setPestana} icono={<User className="w-4 h-4" />}>
              Datos
            </Pestanya>
            <Pestanya actual={pestana} valor="avatar" onClick={setPestana} icono={<Smile className="w-4 h-4" />}>
              Avatar
            </Pestanya>
            <Pestanya actual={pestana} valor="ia" onClick={setPestana} icono={<Sparkles className="w-4 h-4" />}>
              IA
            </Pestanya>
            <Pestanya actual={pestana} valor="seguridad" onClick={setPestana} icono={<Shield className="w-4 h-4" />}>
              Seguridad
            </Pestanya>
            <Pestanya actual={pestana} valor="privacidad" onClick={setPestana} icono={<FileLock className="w-4 h-4" />}>
              Privacidad
            </Pestanya>
          </div>
        </div>

        {pestana === 'datos' && (
          <PanelDatos profile={profile} onProfileChange={onProfileChange} />
        )}
        {pestana === 'avatar' && (
          <PanelAvatar profile={profile} onProfileChange={onProfileChange} />
        )}
        {pestana === 'ia' && <AiKeyPanel profile={profile} onProfileChange={onProfileChange} />}
        {pestana === 'seguridad' && <PanelSeguridad profile={profile} />}
        {pestana === 'privacidad' && <AccountDataPanel profile={profile} onDeleted={onAccountDeleted} />}
      </div>
    </div>
  );
};

const Pestanya: React.FC<{
  actual: Pestana;
  valor: Pestana;
  onClick: (p: Pestana) => void;
  icono: React.ReactNode;
  children: React.ReactNode;
}> = ({ actual, valor, onClick, icono, children }) => (
  <button
    role="tab"
    aria-selected={actual === valor}
    onClick={() => onClick(valor)}
    title={typeof children === 'string' ? children : undefined}
    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-[0.8125rem] font-medium transition-colors ${
      actual === valor ? 'bg-ink-700 text-ink-50' : 'text-ink-400 hover:text-ink-100'
    }`}
  >
    {icono}
    {/* Cinco pestañas no caben escritas en un móvil: ahí, solo el icono. */}
    <span className="sr-only sm:not-sr-only">{children}</span>
  </button>
);

/* ------------------------------------------------------------------ *
 * Datos
 * ------------------------------------------------------------------ */

const PanelDatos: React.FC<{
  profile: Profile;
  onProfileChange: (p: Profile) => void;
}> = ({ profile, onProfileChange }) => {
  const [username, setUsername] = useState(profile.username);
  const [firstName, setFirstName] = useState(profile.firstName ?? '');
  const [lastName, setLastName] = useState(profile.lastName ?? '');
  const [country, setCountry] = useState(profile.country ?? '');
  const [libre, setLibre] = useState<boolean | null>(null);
  const [estado, setEstado] = useState<Estado>({ tipo: 'quieto' });

  const tocado = () => setEstado({ tipo: 'quieto' });

  // Disponibilidad del usuario mientras se escribe, igual que en el registro.
  // El propio no se consulta: es suyo, y preguntarlo lo daría por ocupado.
  useEffect(() => {
    if (username === profile.username || username.length < 3) {
      setLibre(null);
      return;
    }
    const t = setTimeout(() => {
      void (async () => {
        try {
          const data = await api.post<{ available: boolean }>('/auth/check-username', { username });
          setLibre(data.available);
        } catch {
          setLibre(null);
        }
      })();
    }, 350);
    return () => clearTimeout(t);
  }, [username, profile.username]);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEstado({ tipo: 'guardando' });
    try {
      const data = await api.patch<{ profile: Profile }>('/profile', {
        username,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        country,
      });
      onProfileChange(data.profile);
      setEstado({ tipo: 'guardado' });
    } catch (err) {
      setEstado({ tipo: 'error', mensaje: err instanceof Error ? err.message : 'No se pudo guardar.' });
    }
  };

  const sinCambios =
    username === profile.username &&
    firstName === (profile.firstName ?? '') &&
    lastName === (profile.lastName ?? '') &&
    country === (profile.country ?? '');

  return (
    <form onSubmit={guardar} className="px-6 py-5 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nombre">
          <input
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              tocado();
            }}
            maxLength={40}
            required
            autoComplete="given-name"
            className={INPUT}
          />
        </Field>
        <Field label="Apellido">
          <input
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              tocado();
            }}
            maxLength={40}
            required
            autoComplete="family-name"
            className={INPUT}
          />
        </Field>
      </div>
      <p className="-mt-2 text-[0.6875rem] text-ink-500">
        Tu nombre real no se muestra a nadie. En las partidas solo aparece tu usuario.
      </p>

      <Field
        label="Usuario"
        hint="3 a 20 caracteres: letras, números, punto o guion bajo. Es también el nombre que verán los demás en las partidas y en el ranking."
      >
        <div className="relative">
          <input
            value={username}
            // Se filtra al escribir con la MISMA regla que aplica el servidor.
            onChange={(e) => {
              setUsername(
                e.target.value
                  .normalize('NFD')
                  .replace(/[̀-ͯ]/g, '')
                  .replace(/[^A-Za-z0-9_.]/g, ''),
              );
              tocado();
            }}
            maxLength={20}
            required
            className={`${INPUT} pr-10`}
          />
          {libre !== null && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {libre ? (
                <Check className="w-4 h-4 text-success-400" aria-label="Disponible" />
              ) : (
                <X className="w-4 h-4 text-danger-400" aria-label="No disponible" />
              )}
            </span>
          )}
        </div>
      </Field>

      <CountryField
        value={country}
        onChange={(v) => {
          setCountry(v);
          tocado();
        }}
      />

      {/* Lo que no se puede cambiar, dicho aquí en vez de dejar que se busque
          dónde se cambia. */}
      <dl className="rounded-lg border border-ink-800 divide-y divide-ink-800 text-[0.8125rem]">
        {profile.email && <SoloLectura etiqueta="Correo" valor={profile.email} />}
        <SoloLectura
          etiqueta="Edad"
          valor={profile.age !== null ? `${profile.age} años` : '—'}
          nota="No se puede cambiar"
        />
      </dl>

      <PieDeFormulario
        estado={estado}
        deshabilitado={
          sinCambios ||
          username.length < 3 ||
          libre === false ||
          firstName.trim().length < 2 ||
          lastName.trim().length < 2
        }
        pista={sinCambios ? 'Cambia algo para poder guardar.' : undefined}
      />
    </form>
  );
};

const SoloLectura: React.FC<{ etiqueta: string; valor: string; nota?: string }> = ({
  etiqueta,
  valor,
  nota,
}) => (
  <div className="flex items-baseline gap-3 px-3.5 py-2.5">
    <dt className="text-ink-500 shrink-0">{etiqueta}</dt>
    <dd className="ml-auto text-ink-300 text-right truncate">
      {valor}
      {nota && <span className="block text-[0.6875rem] text-ink-500">{nota}</span>}
    </dd>
  </div>
);

/* ------------------------------------------------------------------ *
 * Avatar
 * ------------------------------------------------------------------ */

const PanelAvatar: React.FC<{
  profile: Profile;
  onProfileChange: (p: Profile) => void;
}> = ({ profile, onProfileChange }) => {
  // El taller enseña el guardado y los errores junto a su botón; aquí solo se
  // habla con el servidor.
  const guardar = async (avatar: AvatarConfig) => {
    const data = await api.patch<{ profile: Profile }>('/profile', { avatar });
    onProfileChange(data.profile);
  };

  return (
    <div className="px-6 py-5">
      <AvatarCustomizer initialConfig={profile.avatar} onSave={guardar} embedded />
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * Seguridad
 * ------------------------------------------------------------------ */

const PanelSeguridad: React.FC<{ profile: Profile }> = ({ profile }) => {
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [estado, setEstado] = useState<Estado>({ tipo: 'quieto' });

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nueva !== repetida) {
      return setEstado({ tipo: 'error', mensaje: 'Las dos contraseñas nuevas no coinciden.' });
    }
    setEstado({ tipo: 'guardando' });
    try {
      await api.post<{ closedSessions: number }>('/profile/password', {
        currentPassword: actual,
        newPassword: nueva,
      });
      setActual('');
      setNueva('');
      setRepetida('');
      setEstado({ tipo: 'guardado' });
    } catch (err) {
      setEstado({ tipo: 'error', mensaje: err instanceof Error ? err.message : 'No se pudo cambiar.' });
    }
  };

  return (
    <form onSubmit={guardar} className="px-6 py-5 space-y-4">
      {profile.hasPassword ? (
        <Field label="Contraseña actual">
          <input
            type="password"
            value={actual}
            onChange={(e) => setActual(e.target.value)}
            autoComplete="current-password"
            required
            className={INPUT}
          />
        </Field>
      ) : (
        <p className="flex items-start gap-2 rounded-lg border border-ink-700 bg-ink-850 px-3 py-2.5 text-[0.8125rem] text-ink-300">
          <Lock className="w-4 h-4 shrink-0 mt-px text-ink-500" />
          Entraste con Google, así que tu cuenta todavía no tiene contraseña. Si le pones una,
          podrás entrar también sin Google.
        </p>
      )}

      <Field label="Contraseña nueva" hint="Mínimo 8 caracteres">
        <input
          type="password"
          value={nueva}
          onChange={(e) => setNueva(e.target.value)}
          autoComplete="new-password"
          required
          minLength={8}
          className={INPUT}
        />
      </Field>

      <Field label="Repite la contraseña nueva">
        <input
          type="password"
          value={repetida}
          onChange={(e) => setRepetida(e.target.value)}
          autoComplete="new-password"
          required
          minLength={8}
          className={INPUT}
        />
      </Field>

      <p className="text-[0.6875rem] leading-relaxed text-ink-500">
        Al cambiarla se cierran las sesiones abiertas en otros dispositivos. Esta se queda.
      </p>

      <PieDeFormulario
        estado={estado}
        deshabilitado={!nueva || !repetida || (profile.hasPassword && !actual)}
        etiqueta={profile.hasPassword ? 'Cambiar contraseña' : 'Establecer contraseña'}
        etiquetaHecho="Contraseña actualizada"
      />
    </form>
  );
};

/* ------------------------------------------------------------------ *
 * Comunes
 * ------------------------------------------------------------------ */

type Estado =
  | { tipo: 'quieto' }
  | { tipo: 'guardando' }
  | { tipo: 'guardado' }
  | { tipo: 'error'; mensaje: string };

const PieDeFormulario: React.FC<{
  estado: Estado;
  deshabilitado: boolean;
  etiqueta?: string;
  etiquetaHecho?: string;
  /** Por que el boton esta apagado. Uno gris sin explicacion parece roto. */
  pista?: string;
}> = ({
  estado,
  deshabilitado,
  etiqueta = 'Guardar cambios',
  etiquetaHecho = 'Guardado',
  pista,
}) => (
  <>
    {estado.tipo === 'error' && (
      <p
        role="alert"
        className="flex items-start gap-2 rounded-lg border border-danger-400/30 bg-danger-400/10 px-3 py-2.5 text-danger-400 text-[0.8125rem]"
      >
        <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
        {estado.mensaje}
      </p>
    )}

    <div className="flex items-center gap-3">
      <button
        type="submit"
        disabled={deshabilitado || estado.tipo === 'guardando'}
        className="flex items-center justify-center gap-2 px-5 h-11 rounded-lg font-semibold text-sm
                   bg-ember-500 hover:bg-ember-400 text-on-accent
                   disabled:bg-ink-800 disabled:text-ink-500 disabled:cursor-not-allowed transition-colors"
      >
        {estado.tipo === 'guardando' && <Loader2 className="w-4 h-4 animate-spin" />}
        {etiqueta}
      </button>

      {estado.tipo === 'guardado' ? (
        <span className="flex items-center gap-1.5 text-success-400 text-[0.8125rem]">
          <Check className="w-4 h-4" />
          {etiquetaHecho}
        </span>
      ) : (
        deshabilitado && pista && <span className="text-ink-500 text-[0.8125rem]">{pista}</span>
      )}
    </div>
  </>
);

/** Miniatura reutilizable para la tarjeta de identidad del perfil. */
export const AvatarPreview: React.FC<{ profile: Profile; size?: number }> = ({
  profile,
  size = 88,
}) => <Avatar config={profile.avatar as never} size={size} />;
